import { memo, RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { VoicemailBox } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/voicemail.pb';
import {
  DeleteVoicemailResponse,
  SetVoicemailViewStatusResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import {
  InfiniteQueryObserverResult,
  UseInfiniteQueryResult,
  UseMutateAsyncFunction,
  UseMutateFunction,
} from 'react-query';
import { VoiceMailboxApi } from '@frontend/api-voicemail-boxes';
import { VoicemailTypes } from '@frontend/api-voicemails';
import { EmptyStateConfig, InfinitePaginatedList, RenderListItemProps } from '@frontend/components';
import { ActionsUI } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { ScopedLocationFilterMenu } from '@frontend/location-filter-menu';
import { CachedAudioScrubber } from '@frontend/phone-panels';
import { Photos } from '@frontend/photos';
import { useScopedQuery } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import {
  Avatar,
  BaseFieldProps,
  Button,
  CopyableText,
  PhoneOutgoingIcon,
  SearchField,
  useFormField,
  useModalControl,
  Text,
  styles,
  TextLink,
} from '@frontend/design-system';
import { VoicemailFiltersType } from '../../hooks/use-phone-config-state-store';
import { queryKeys } from '../../query-keys';
import { getActionIcon } from '../../ui-utils';
import { Action, CustomActionCell } from '../all-calls/shared-buttons';
import { VoicemailInfiniteQueryData, VoicemailTableDataRow } from '../all-calls/types';
import { TranscriptionContext } from '../voicemail-details/types';
import { getTranscriptionUniqueKey } from '../voicemail-details/utils';
import { VoicemailsTrayFilters } from './vm-filters';
import { VoicemailActionsGetterProps } from './voicemail-actions';

export const SmallView = memo(
  ({
    getVoicemailActions,
    selectedLocationIds,
    setFilters,
    filters,
    infiniteQueryProps,

    defaultFilters,
    voicemailBoxList,
    mappedTags,

    searchHandler,
    // emptyState,

    listHeight,
    panelProps,
  }: {
    getVoicemailActions: (props: VoicemailActionsGetterProps) => Action[];
    selectedLocationIds: string[];
    setFilters: (filters: VoicemailFiltersType) => void;
    filters: VoicemailFiltersType;
    infiniteQueryProps: UseInfiniteQueryResult<VoicemailInfiniteQueryData, unknown>;

    defaultFilters: VoicemailFiltersType;
    voicemailBoxList: VoicemailBox[];
    mappedTags: Record<string, Tag>;

    searchHandler: (searchText: string) => void;
    emptyState: EmptyStateConfig;

    listHeight?: number;
    panelProps: PanelProps;
  }) => {
    const { t } = useTranslation('calls');
    // const { getScopeName } = useAppScopeStore();
    const searchField = useFormField(
      { type: 'text', placeholder: t('Search name or 10-digit number'), value: filters.filterOptions.callerName },
      [filters.filterOptions.callerName]
    );

    const [shouldShowSearch, setShouldShowSearch] = useState(false);

    const [selectedIndex, setSelectedIndex] = useState(-1);

    const [callActionContext, setCallActionContext] = useState<{
      personId: string;
      phoneNumber: string;
      locationId: string;
    }>({
      personId: '',
      phoneNumber: '',
      locationId: '',
    });

    const { triggerProps: callTriggerProps, Modal: PhoneCallModal } = ActionsUI.actions.usePhoneCallAction({
      context: callActionContext,
    });

    const allDataRows = useMemo(() => {
      if (!infiniteQueryProps.data?.pages) return [];
      return infiniteQueryProps.data.pages.flatMap((page) => page.data || []);
    }, [infiniteQueryProps]);

    const renderListItem = useMemo(
      () =>
        getRenderListItem({
          getVoicemailActions,
          triggerCall: (context) => {
            setCallActionContext({
              personId: context.personId,
              phoneNumber: context.phoneNumber,
              locationId: context.locationId,
            });
            callTriggerProps?.onClick();
          },
          setSelectedIndex,
          panelProps: {
            ...panelProps,
            rows: allDataRows,
            getNextPage: infiniteQueryProps.fetchNextPage,
          },
        }),
      [getVoicemailActions, panelProps, allDataRows, infiniteQueryProps.fetchNextPage, callTriggerProps]
    );

    useLayoutEffect(() => {
      if (selectedIndex > -1 && selectedIndex < allDataRows.length) {
        const element = document.querySelector(`li[data-index="${selectedIndex}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, [allDataRows.length, selectedIndex]);

    return (
      <div>
        <div
          css={{
            padding: theme.spacing(0, 2, 2),
            display: 'grid',
            gap: theme.spacing(1),
            borderBottom: `1px solid ${theme.colors.neutral20}`,
          }}
        >
          {shouldShowSearch ? (
            <SearchBar
              label={t('Done')}
              onDismissClick={() => setShouldShowSearch(false)}
              field={searchField}
              searchHandler={(searchText) => {
                searchHandler(searchText);
              }}
            />
          ) : (
            <ActionBar
              selectedLocations={selectedLocationIds}
              onLocationChange={(locations) => {
                setFilters({
                  ...filters,
                  locationIds: locations,
                });
              }}
              onSearchClick={() => setShouldShowSearch(true)}
              shouldShowSearchBadge={!!filters.filterOptions.callerName}
              filterProps={{
                defaultFilters,
                filters,
                mailboxes: voicemailBoxList,
                onChange: setFilters,
                isMulti: selectedLocationIds.length > 1,
                mappedTags,
              }}
            />
          )}
        </div>
        <div>
          <InfinitePaginatedList
            height={listHeight}
            infiniteQueryProps={infiniteQueryProps}
            renderListItem={renderListItem}
          />
          {PhoneCallModal}
        </div>
      </div>
    );
  }
);
SmallView.displayName = 'VoicemailSmallView';

type PanelProps = {
  updateReadStatus: UseMutateAsyncFunction<
    SetVoicemailViewStatusResponse,
    unknown,
    VoicemailTypes.StatusPayload,
    unknown
  >;
  deleteVoicemail: UseMutateFunction<DeleteVoicemailResponse, unknown, VoicemailTypes.DeletePayload, void>;
  voicemailsQueryKey: string[];
  sectionFocus: TranscriptionContext['sectionFocus'];
};

const VoicemailListItem = memo(
  ({
    listItem,
    index,
    getVoicemailActions,
    triggerCall,
    setSelectedIndex,
    panelProps,
  }: {
    listItem: VoicemailTableDataRow;
    index: number;
    getVoicemailActions: (props: VoicemailActionsGetterProps) => Action[];
    triggerCall: (context: { personId: string; phoneNumber: string; locationId: string }) => void;
    setSelectedIndex: (index: number) => void;
    panelProps: PanelProps & {
      rows: VoicemailTableDataRow[];
      getNextPage: () => Promise<InfiniteQueryObserverResult<VoicemailInfiniteQueryData, unknown>>;
    };
  }) => {
    const { firstName, lastName, locationIds } = listItem;
    const textRef = useRef<HTMLSpanElement>(null);

    const { data: transcription } = useScopedQuery({
      queryKey: queryKeys.voicemailTranscript(listItem.channelId),
      queryFn: async () => {
        const response = await VoiceMailboxApi.getTranscription(listItem.channelId);
        return response;
      },
      retry: false,
      enabled: !!listItem,
      staleTime: Infinity,
      cacheTime: Infinity,
    });

    const { setShow } = useSlidePanelShallowStore<TranscriptionContext>('setShow', 'panelType', 'show', 'context');

    const { actions, markReadAction }: { actions: Action[]; markReadAction?: Action } = useMemo(() => {
      const allActions = getVoicemailActions({
        index,
        rowData: listItem,
        triggerCall: () => {
          triggerCall({
            personId: listItem.contactId || '',
            phoneNumber: listItem.contactNumber,
            locationId: locationIds[0],
          });
        },
        addTag: () => {
          // noop, since we're removing it anyway
        },
      }).filter((action) => action.id !== 'add-tag-action'); // Exclude add tag action from the list

      const showPanel = () =>
        setShow(true, 'voicemailTranscription', {
          ...panelProps,
          instanceId: 'table',
          currentRowId: getTranscriptionUniqueKey(listItem),
          toggleRowSelected: (_, _1, index) => {
            if (index === undefined) {
              return;
            }
            setSelectedIndex(index);
          },
          userHasAccess: true,
        });

      allActions.unshift({
        id: 'view-details-action',
        label: 'View Voicemail Details',
        Icon: () => getActionIcon({ iconName: 'document-filled' }),
        onClick: showPanel,
        trackingId: 'phone-portal-weave2.0-voicemails-view-details',
      });

      return { actions: allActions, markReadAction: allActions.find((action) => action.id === 'mark-read-action') };
    }, [getVoicemailActions, index, listItem, triggerCall, locationIds, setShow, panelProps, setSelectedIndex]);

    const [willOverflow, setWillOverflow] = useState(false);

    useLayoutEffect(() => {
      if (textRef.current && transcription?.text) {
        // Create a temporary element to measure text width
        const tempElement = document.createElement('span');
        tempElement.style.visibility = 'hidden';
        tempElement.style.position = 'absolute';
        tempElement.style.whiteSpace = 'nowrap';
        tempElement.style.fontSize = getComputedStyle(textRef.current).fontSize;
        tempElement.style.fontFamily = getComputedStyle(textRef.current).fontFamily;
        tempElement.style.fontWeight = getComputedStyle(textRef.current).fontWeight;
        tempElement.textContent = transcription.text;

        document.body.appendChild(tempElement);
        const textWidth = tempElement.getBoundingClientRect().width;
        document.body.removeChild(tempElement);

        const containerWidth = textRef.current.parentElement?.getBoundingClientRect().width || 0;
        setWillOverflow(textWidth > containerWidth);
      }
    }, [transcription?.text]);

    return (
      <div
        css={{
          display: 'grid',
          padding: theme.spacing(2),
          gridTemplateAreas: '"photo name actions" ". details details" ". meta meta"',
          gridTemplateColumns: 'auto 1fr auto',
          gridTemplateRows:
            '[first-start] auto [first-end] 0px [second-start] auto [second-end] 8px [third-start] auto [third-end]',
          columnGap: theme.spacing(1),
          alignItems: 'center',
          borderBottom: `1px solid ${theme.colors.neutral20}`,
        }}
      >
        <Photos.ContactProfilePhoto
          css={{ gridArea: 'photo', gridRow: 'first' }}
          locationId={locationIds[0]}
          personId={listItem.contactId || ''}
          name={listItem.contactName}
          disableClick={!listItem.contactId}
        >
          {!listItem.readAt && <Avatar.Dot variant='primary' />}
        </Photos.ContactProfilePhoto>
        <NamePlate
          firstName={firstName}
          lastName={lastName}
          fallback={listItem.contactName || listItem.contactNumber}
          css={{ gridArea: 'name', gridRow: 'first' }}
        />

        {actions.length > 0 && <Actions actions={actions} />}
        <div
          css={{ gridArea: 'details', display: 'grid', alignItems: 'center', gap: theme.spacing(1), gridRow: 'second' }}
        >
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: 'max-content max-content max-content',
              alignItems: 'center',
              gap: theme.spacing(1),
            }}
          >
            <Text size='medium'>{listItem.dateTime}</Text>•<Text size='medium'>{listItem.contactNumber}</Text>
          </div>
          <CachedAudioScrubber
            filePath={listItem.mediaFilePath}
            mediaId={listItem.mediaId}
            onPlay={() => {
              markReadAction?.onClick?.();
            }}
          />
        </div>
        <div css={{ gridArea: 'meta', display: 'grid', alignItems: 'center', gap: theme.spacing(1), gridRow: 'third' }}>
          {transcription?.text &&
            (willOverflow ? (
              <OverflowingText
                textRef={textRef}
                text={transcription.text}
                markAsRead={markReadAction?.onClick ?? (() => {})}
              />
            ) : (
              <Text ref={textRef} css={styles.truncate} as='span'>
                {transcription.text}
              </Text>
            ))}
        </div>
      </div>
    );
  }
);

VoicemailListItem.displayName = 'VoicemailListItem';

const getRenderListItem =
  ({
    getVoicemailActions,
    triggerCall,
    setSelectedIndex,
    panelProps,
  }: {
    getVoicemailActions: (props: VoicemailActionsGetterProps) => Action[];
    triggerCall: (context: { personId: string; phoneNumber: string; locationId: string }) => void;
    setSelectedIndex: (index: number) => void;
    panelProps: PanelProps & {
      rows: VoicemailTableDataRow[];
      getNextPage: () => Promise<InfiniteQueryObserverResult<VoicemailInfiniteQueryData, unknown>>;
    };
  }) =>
  // eslint-disable-next-line react/display-name
  ({ listItem, index }: RenderListItemProps<VoicemailTableDataRow>) => {
    return (
      <VoicemailListItem
        listItem={listItem}
        index={index}
        setSelectedIndex={setSelectedIndex}
        getVoicemailActions={getVoicemailActions}
        triggerCall={triggerCall}
        panelProps={panelProps}
      />
    );
  };

const OverflowingText = memo(
  ({ text, textRef, markAsRead }: { text: string; textRef: RefObject<HTMLSpanElement>; markAsRead: () => void }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { t } = useTranslation('calls');

    if (isExpanded) {
      return (
        <div
          css={{
            display: 'grid',
            gridTemplateRows: '1fr auto',
          }}
        >
          <Text ref={textRef} as='span'>
            {text}
          </Text>
          <TextLink
            css={{ justifySelf: 'end' }}
            weight='bold'
            onClick={() => {
              setIsExpanded(false);
            }}
          >
            {t('Less')}
          </TextLink>
        </div>
      );
    }

    return (
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr max-content',
          alignItems: 'center',
          gap: theme.spacing(1),
          width: '100%',
        }}
      >
        <Text ref={textRef} as='span' css={styles.truncate}>
          {text}
        </Text>
        <TextLink
          weight='bold'
          onClick={() => {
            setIsExpanded(true);
            markAsRead?.();
          }}
        >
          {t('More')}
        </TextLink>
      </div>
    );
  }
);

OverflowingText.displayName = 'OverflowingText';

const Actions = ({ className, actions }: { className?: string; actions: Action[] }) => {
  // Separate call and message actions from the rest of the actions
  const { callAction, messageAction, remainingActions } = useMemo(
    () =>
      actions.reduce(
        (acc, action) => {
          if (action.id === 'call-action') {
            acc.callAction = action;
          } else if (action.id === 'message-action') {
            acc.messageAction = action;
          } else {
            acc.remainingActions.push(action);
          }
          return acc;
        },
        { callAction: undefined, messageAction: undefined, remainingActions: [] } as {
          callAction?: Action;
          messageAction?: Action;
          remainingActions: Action[];
        }
      ),
    [actions]
  );

  return (
    <div className={className} css={{ display: 'grid', gridTemplateColumns: 'repeat(3, max-content)' }}>
      {callAction && !callAction.hide && (
        <Button
          variant='secondary'
          iconName='phone'
          size='large'
          css={{ height: 32, width: 32 }}
          onClick={callAction.onClick}
          trackingId={callAction.trackingId}
          disabled={callAction.disabled}
        />
      )}
      {messageAction && !messageAction.hide && (
        <Button
          variant='secondary'
          iconName='messaging'
          size='large'
          css={{ height: 32, width: 32 }}
          onClick={messageAction.onClick}
          trackingId={messageAction.trackingId}
          disabled={messageAction.disabled}
        />
      )}

      <CustomActionCell actions={remainingActions} tableTrackingId={'phone-portal-weave2.0-voicemails'} />
    </div>
  );
};

Actions.displayName = 'Actions';

const NamePlate = ({
  firstName,
  lastName,
  fallback,
  direction,
  className,
}: {
  firstName?: string;
  lastName?: string;
  fallback?: string;
  direction?: 'outbound' | 'inbound';
  className?: string;
}) => {
  const { t } = useTranslation('calls');
  let content;
  if (!firstName && !lastName) {
    content = (
      <Text as='span' size={'medium'}>
        {fallback || t('Unknown')}
      </Text>
    );
  } else {
    content = (
      <CopyableText tooltipSuccessText={t('Copied')} as='span'>
        {!!firstName && (
          <Text as='span' size={'medium'} weight='bold'>
            {firstName}
          </Text>
        )}

        {firstName && lastName ? ' ' : null}

        {!!lastName && (
          <Text as='span' size={'medium'}>
            {lastName}
          </Text>
        )}
      </CopyableText>
    );
  }

  return (
    <div
      className={className}
      css={{
        display: 'grid',
        gridTemplateColumns: 'max-content max-content',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      {content}
      {direction === 'outbound' && <PhoneOutgoingIcon size={16} />}
    </div>
  );
};

const SearchBar = ({
  label,
  onDismissClick,
  field,
  searchHandler,
}: {
  label: string;
  onDismissClick: () => void;
  field: BaseFieldProps<string>;
  searchHandler: (searchText: string) => void;
}) => {
  return (
    <div css={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: theme.spacing(1) }}>
      <SearchField
        {...field}
        autoFocus
        onChange={(e) => {
          field.onChange(e);

          if (!e) {
            return;
          }

          if ('target' in e) {
            searchHandler((e.target as HTMLInputElement).value);
          } else {
            searchHandler(e.value);
          }
        }}
        label=''
        name='search'
      />
      <Button variant='secondary' size='large' onClick={onDismissClick}>
        {label}
      </Button>
    </div>
  );
};

const ActionBar = ({
  selectedLocations,
  onLocationChange,
  onSearchClick,
  shouldShowSearchBadge,
  filterProps,
}: {
  selectedLocations: string[];
  onLocationChange: (locations: string[]) => void;
  onSearchClick: () => void;
  shouldShowSearchBadge: boolean;
  filterProps: {
    defaultFilters: VoicemailFiltersType;
    filters: VoicemailFiltersType;
    mailboxes: VoicemailBox[];
    onChange: (filters: VoicemailFiltersType) => void;
    isMulti: boolean;
    mappedTags: Record<string, Tag>;
  };
}) => {
  const { modalProps, triggerProps } = useModalControl();
  const [showFilterBadge, setShowFilterBadge] = useState(false);

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gridTemplateAreas: '"location search filter"',
      }}
    >
      <ScopedLocationFilterMenu
        selectedOptions={selectedLocations}
        trackingIdBase={'phone-weave2.0-voicemails'}
        onChange={onLocationChange}
        showClearAll={false}
        css={{ gridArea: 'location' }}
      />
      <Button
        variant='secondary'
        iconName='search'
        size='large'
        css={[
          { gridArea: 'search' },
          css`
            ::after {
              position: absolute;
              opacity: ${shouldShowSearchBadge ? 1 : 0};
              background: ${theme.colors.primary50};
              border-radius: 16px;
              border: 1.5px solid ${theme.colors.white};
              height: ${theme.spacing(1.5)};
              width: ${theme.spacing(1.5)};
              top: 6px;
              left: ${theme.spacing(3)};
              z-index: ${theme.zIndex.high};
              content: '';
              transition: border-color, opacity ease-in-out 250ms;
            }
            :hover::after,
            :focus::after {
              border-color: ${theme.colors.neutral10};
            }
          `,
        ]}
        onClick={onSearchClick}
      />
      <Button
        variant='secondary'
        iconName='filter-alt'
        size='large'
        css={[
          { gridArea: 'filter' },
          css`
            ::after {
              position: absolute;
              opacity: ${showFilterBadge ? 1 : 0};
              background: ${theme.colors.primary50};
              border-radius: 16px;
              border: 1.5px solid ${theme.colors.white};
              height: ${theme.spacing(1.5)};
              width: ${theme.spacing(1.5)};
              top: 6px;
              left: ${theme.spacing(3)};
              z-index: ${theme.zIndex.high};
              content: '';
              transition: border-color, opacity ease-in-out 250ms;
            }
            :hover::after,
            :focus::after {
              border-color: ${theme.colors.neutral10};
            }
          `,
        ]}
        {...triggerProps}
      />
      <VoicemailsTrayFilters {...filterProps} modalProps={modalProps} setShowNotificationBadge={setShowFilterBadge} />
    </div>
  );
};
